/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useState } from 'react'

import { DetailSheet } from '@/components/ui/detail-sheet/DetailSheet'

import { TrackerDetailBody, TrackerDetailHeader, type TrackerDetailTab } from './TrackerDetailContent'
import { useTrackerMapContext } from './TrackerMapContext'
import { rowId } from './utils'

interface TrackerDetailSheetProps {
  variant?: 'overlay' | 'pane'
  /** The tracker table's header row, relative to the sheet's positioned container. */
  tableHeader?: { top: number; height: number; left: number }
}

/**
 * The tracker details beside the list. As an overlay it starts after the table's pinned columns
 * (Shipment, Status, Alert); in the split layout it takes the map's place.
 */
export const TrackerDetailSheet = ({ variant = 'overlay', tableHeader }: TrackerDetailSheetProps) => {
  const context = useTrackerMapContext()
  const row = context.currentRow
  const isOpen = context.detailSheetIsOpen && !!row
  // Held here rather than in the body, so the tab survives switching trackers.
  const [activeTab, setActiveTab] = useState<TrackerDetailTab>('status')

  return (
    <DetailSheet
      isOpen={isOpen}
      variant={variant}
      tableHeader={tableHeader}
      fallbackLeft={374}
      bodyKey={row ? rowId(row) : undefined}
      header={row && <TrackerDetailHeader row={row} onClose={() => context.setDetailSheetIsOpen(false)} />}
    >
      {row && (
        <TrackerDetailBody
          row={row}
          tracker={context.currentTrackerData}
          show={isOpen}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          compact
        />
      )}
    </DetailSheet>
  )
}
